import { useGame } from "~/app";
import {
  ActionSlot,
  AgentEffectChoices,
  AgentEffectCommands,
  Card,
  EffectChoiceOption,
  EffectChoiceType,
  ResourceType,
  sellSpiceAmounts,
} from "~/business";
import { useViewingPlayerOpt } from "~/jsx/hook";
import { Sizes } from "~/jsx/theme";
import { Pile, Resources, expectNever, parseEnumOrError } from "~/utils";
import {
  XActionSlot,
  XAgentEffect,
  XCard,
  XCardBack,
  XDreadnought,
  XFactionChange,
  XIntrigueCard,
  XIntrigueCardSvg,
  XPlayerTroop,
  XResourceWithAmount,
  XTechTile,
  XTypography,
} from "../design";

const cardSize = 128;

const { cause, spice, solaris } = AgentEffectCommands;

const getChoiceOption = <Key extends keyof typeof AgentEffectChoices>(
  option: EffectChoiceOption
) => {
  return option as EffectChoiceOption<Key>;
};

export const XChoice = ({
  value,
  playerId,
  choiceType,
}: {
  value: EffectChoiceOption;
  playerId: string;
  choiceType: EffectChoiceType;
}) => {
  const viewingPlayer = useViewingPlayerOpt();
  const { game } = useGame();

  switch (choiceType) {
    case "freeCard":
    case "trashMe":
    case "reserveShopCardWithDiscount":
    case "opponentsDiscard":
    case "drawBenegesseritCardFromDiscard":
    case "discardCard":
    case "trashCard": {
      const { cardId } = getChoiceOption<typeof choiceType>(value);
      const card = game.findCard(cardId);
      if (!card) throw new Error(`could not find card with id ${cardId}`);
      return <XChoiceCard card={card} choosingPlayerId={playerId} />;
    }
    case "acquireTechTile": {
      const { techNegociators } = game.getPlayer(playerId);
      const { techTileId, discount = 0 } =
        getChoiceOption<typeof choiceType>(value);
      const techTile = game.getTechTile(techTileId, true);
      const { cost } = techTile;
      const finalCost = Math.max(0, cost - discount - techNegociators.length);
      return (
        <div>
          <XTechTile techTile={techTile} />
          <XTypography>cost: {finalCost}</XTypography>
        </div>
      );
    }
    case "recallAgent":
    case "dreadnoughtControlSlot":
    case "blockActionSlot": {
      const { slotId } = getChoiceOption<typeof choiceType>(value);
      const actionSlot = game.getActionSlot(slotId);
      return (
        <XActionSlot
          actionSlot={new ActionSlot({ ...actionSlot, meaples: Pile.empty() })}
        />
      );
    }
    case "buyOnDeck": {
      const { cardId } = getChoiceOption<typeof choiceType>(value);
      const player = game.getPlayer(playerId);
      const card = player.getCard(cardId);
      return (
        <div>
          <XTypography>Put this card on top of your deck</XTypography>
          <XChoiceCard card={card} choosingPlayerId={playerId} />
        </div>
      );
    }
    case "canDeployToConflict": {
      const { troopCount, type } = getChoiceOption<typeof choiceType>(value);
      return (
        <XResourceWithAmount
          type={type}
          amount={troopCount}
          width={32}
          variant="separated"
        />
      );
    }
    case "canPassTurn": {
      const { passTurn } = getChoiceOption<typeof choiceType>(value);
      return (
        <XTypography>{passTurn ? "Pass turn" : "Do not pass turn"}</XTypography>
      );
    }
    case "canRetreat": {
      const { troopCount } = getChoiceOption<typeof choiceType>(value);
      return (
        <XResourceWithAmount
          type="troop"
          amount={troopCount}
          width={32}
          variant="separated"
        />
      );
    }
    case "chooseFactionChange": {
      const { amount, faction } = getChoiceOption<typeof choiceType>(value);
      return <XFactionChange change={amount} width={48} faction={faction} />;
    }
    case "chooseLooseConflictTroops": {
      const { looseConflictTroops } = getChoiceOption<typeof choiceType>(value);
      return (
        <div style={{ display: "flex", alignItems: "center" }}>
          <XTypography>Loose {looseConflictTroops} </XTypography>
          <XPlayerTroop playerId={playerId} width={24} />
          <XTypography>&nbsp;in the conflict</XTypography>
        </div>
      );
    }
    case "choosePaymentForTechTile": {
      const { cost, resourceType } = getChoiceOption<typeof choiceType>(value);
      return (
        <XResourceWithAmount
          amount={cost}
          type={resourceType}
          width={32}
          variant="compact"
        />
      );
    }
    case "intrigueScry":
    case "discardIntrigueCard": {
      const { intrigueCardId } = getChoiceOption<typeof choiceType>(value);
      const intrigueCard = game.getIntrigueCard(intrigueCardId);
      if (viewingPlayer?.id === playerId) {
        return (
          <XIntrigueCard width={Sizes.intrigueCard.width} card={intrigueCard} />
        );
      } else {
        return <XIntrigueCardSvg width={Sizes.intrigueCard.width} />;
      }
    }
    case "drawOrTrash": {
      const { cardId, action } = getChoiceOption<typeof choiceType>(value);
      const card = game.findCard(cardId);
      if (!card) throw new Error(`could not find card with id ${cardId}`);
      return (
        <div>
          <XTypography>{action}</XTypography>
          <XChoiceCard card={card} choosingPlayerId={playerId} />
        </div>
      );
    }
    case "ennemyRetreatNUnit": {
      const { unitCount } = getChoiceOption<typeof choiceType>(value);
      return (
        <>
          <XResourceWithAmount
            type="troop"
            amount={unitCount}
            width={32}
            variant="separated"
          />
          <XDreadnought width={32} />
        </>
      );
    }
    case "freighterChoice": {
      const { freighter } = getChoiceOption<typeof choiceType>(value);
      return (
        <XTypography>
          {freighter === "up"
            ? "Level up your freighter"
            : "Recall your freighter"}
        </XTypography>
      );
    }
    case "opponentDiscardOrLooseTroop": {
      const option = getChoiceOption<typeof choiceType>(value);
      if ("looseTroop" in option) {
        const { looseTroop } = option;
        return (
          <XTypography style={{ width: 128 }}>
            Loose {looseTroop}{" "}
            <span style={{ verticalAlign: "middle" }}>
              <XPlayerTroop width={24} playerId={playerId} />
            </span>{" "}
            in the conflict
          </XTypography>
        );
      } else {
        return (
          <XChoice playerId={playerId} value={option} choiceType="trashCard" />
        );
      }
    }
    case "orChoice": {
      const { effect } = getChoiceOption<typeof choiceType>(value);
      return (
        <XAgentEffect effectDescription={effect} width={48} context="choice" />
      );
    }
    case "payResource": {
      const { cost, willPay } = getChoiceOption<typeof choiceType>(value);
      const resources = Resources.parse(cost);
      return (
        <>
          <XTypography>{willPay ? "Pay" : "Don't pay"}</XTypography>
          {resources.array.map((pile) => (
            <XResourceWithAmount
              key={pile.name}
              type={parseEnumOrError(ResourceType, pile.name)}
              amount={pile.value}
              width={48}
            />
          ))}
        </>
      );
    }
    case "playAgain": {
      const option = getChoiceOption<typeof choiceType>(value);
      return <XTypography>You may play again</XTypography>;
    }
    case "replaceOpponentConflictTroop": {
      const { opponentId } = getChoiceOption<typeof choiceType>(value);
      return <XPlayerTroop width={32} playerId={opponentId} />;
    }
    case "sellSpice": {
      const { spiceAmount } = getChoiceOption<typeof choiceType>(value);
      const { solaris: solarisAmount = 0 } =
        sellSpiceAmounts.find(({ spice }) => spice === spiceAmount) ?? {};
      const effect = cause(spice(spiceAmount)).effect(solaris(solarisAmount));
      return (
        <XAgentEffect effectDescription={effect} width={20} context="choice" />
      );
    }
    default: {
      expectNever(choiceType);
      throw new Error("unexpected");
    }
  }
};

const XChoiceCard = ({
  card,
  choosingPlayerId,
}: {
  card: Card;
  choosingPlayerId: string;
}) => {
  const { game } = useGame();
  const choosingPlayer = game.getPlayer(choosingPlayerId);
  const { hand } = choosingPlayer;
  const viewingPlayer = useViewingPlayerOpt();
  if (viewingPlayer?.id !== choosingPlayerId && hand.findById(card.id)) {
    return <XCardBack width={cardSize} />;
  } else {
    return <XCard card={card} width={cardSize} removeAnimationId={true} />;
  }
};
